import { INSTRUMENT_TYPE_MAP, INSTRUMENT_TYPES_WITH_SCHEDULE_PLATFORM, SCHEDULE_PLATFORM_TYPE } from 'utils/constants'
import { _getDefaultSchedule } from './PropertyFields'
import { getRandomScheduleId } from './getRandomScheduleId'

export { getRandomScheduleId }

/** Backend expects `schedule_id`; `scheduleId` is only read for tolerance of older payloads. */
export const getScheduleId = schedule => schedule?.schedule_id || schedule?.scheduleId

/**
 * A schedule only carries `version` once it has been saved through an Add/Edit Schedule
 * action. Placeholder schedules written while creating an instrument or saving Properties
 * have no version, so the first real schedule save starts at 1 instead of incrementing.
 */
export const getScheduleVersion = schedule => {
  const version = Number(schedule?.version)
  return Number.isInteger(version) && version > 0 ? version : undefined
}

const getVersionableScheduleContent = schedule => {
  const content = { ...(schedule || {}) }
  delete content.scheduleId
  delete content.schedule_id
  delete content.version
  return content
}

const hasScheduleChanged = (previousSchedule, currentSchedule) =>
  JSON.stringify(getVersionableScheduleContent(previousSchedule)) !==
  JSON.stringify(getVersionableScheduleContent(currentSchedule))

/** Sets `version`, or drops the key entirely for schedules that have never been saved. */
const withScheduleVersion = (schedule, version) => {
  const next = { ...schedule }
  if (version == null) delete next.version
  else next.version = version
  return next
}

const withScheduleIdentity = (schedule, { version } = {}) => {
  const identity = { ...(schedule || {}) }
  delete identity.scheduleId
  return withScheduleVersion(
    {
      ...identity,
      schedule_id: getScheduleId(schedule) || getRandomScheduleId(),
    },
    version == null ? getScheduleVersion(schedule) : version,
  )
}

/**
 * Normalizes metadata.schedule to always return an array.
 * Supports legacy single schedule objects and the multi-schedule array shape.
 */
export const normalizeSchedules = schedule => {
  if (!schedule) return []
  if (Array.isArray(schedule)) return schedule.filter(Boolean)
  if (typeof schedule === 'object') return [schedule]
  return []
}

/**
 * Ensures every schedule entry has a schedule_id, keeping any version it already has.
 */
export const ensureScheduleIds = schedules =>
  normalizeSchedules(schedules).map(schedule => withScheduleIdentity(schedule))

/**
 * Split API schedule (object or array) into:
 * - confirmedSchedules: full list for All Schedules page
 * - activeSchedule: single object for the Scheduler editor
 */
export const hydrateSchedulesFromMetadata = schedule => {
  const list = ensureScheduleIds(schedule)
  if (!list.length) {
    const activeSchedule = withScheduleIdentity(_getDefaultSchedule())
    return { confirmedSchedules: [], activeSchedule }
  }
  return {
    confirmedSchedules: list,
    activeSchedule: { ...list[list.length - 1] },
  }
}

/**
 * Commit the currently edited schedule into the confirmed list.
 * - When activeScheduleIndex is set → update that entry (edit flow)
 * - When activeScheduleIndex is null → always append as a new schedule (new flow)
 *
 * Editing never changes `version`; versions only move when schedules are saved
 * to the database (see applyScheduleVersions).
 */
export const commitScheduleToList = ({
  confirmedSchedules = [],
  currentSchedule,
  activeScheduleIndex,
}) => {
  if (!currentSchedule || typeof currentSchedule !== 'object') return confirmedSchedules

  const next = ensureScheduleIds(confirmedSchedules)

  // Edit existing schedule
  if (
    activeScheduleIndex != null &&
    activeScheduleIndex >= 0 &&
    activeScheduleIndex < next.length
  ) {
    const previousSchedule = next[activeScheduleIndex]
    next[activeScheduleIndex] = withScheduleVersion(
      withScheduleIdentity({
        ...currentSchedule,
        schedule_id: getScheduleId(currentSchedule) || getScheduleId(previousSchedule),
      }),
      getScheduleVersion(previousSchedule),
    )
    return next
  }

  // New schedule: never reuse an existing schedule id. The version is resolved on save.
  let scheduleId = getScheduleId(currentSchedule) || getRandomScheduleId()
  if (next.some(item => getScheduleId(item) === scheduleId)) {
    scheduleId = getRandomScheduleId()
  }
  next.push(
    withScheduleIdentity({
      ...currentSchedule,
      schedule_id: scheduleId,
    }),
  )
  return next
}

const mapSchedulesById = schedules =>
  new Map(normalizeSchedules(schedules).map(schedule => [getScheduleId(schedule), schedule]))

/**
 * Resolve the version to send for an Add Schedule / Edit Schedule save, comparing
 * against the copy currently stored in the database:
 * - added, or stored without a version (placeholder from instrument creation) → version 1
 * - edited since it was last saved → previous version + 1
 * - unchanged → previous version
 */
export const applyScheduleVersions = (schedules, persistedSchedules = []) => {
  const persistedById = mapSchedulesById(persistedSchedules)
  return ensureScheduleIds(schedules).map(schedule => {
    const persisted = persistedById.get(getScheduleId(schedule))
    const previousVersion = getScheduleVersion(persisted)
    if (previousVersion == null) return { ...schedule, version: 1 }
    return {
      ...schedule,
      version: hasScheduleChanged(persisted, schedule) ? previousVersion + 1 : previousVersion,
    }
  })
}

/**
 * Keep the stored version untouched for saves that are not schedule actions (Properties,
 * instrument content, site updates). Schedules never saved through a schedule action stay
 * versionless so their first Add/Edit Schedule save starts at 1.
 */
export const keepPersistedScheduleVersions = (schedules, persistedSchedules = []) => {
  const persistedById = mapSchedulesById(persistedSchedules)
  return ensureScheduleIds(schedules).map(schedule =>
    withScheduleVersion(schedule, getScheduleVersion(persistedById.get(getScheduleId(schedule)))),
  )
}

/**
 * Build a fresh default schedule for "+ New Schedule".
 */
export const createBlankSchedule = instrumentType => {
  const schedule = withScheduleIdentity(_getDefaultSchedule())
  if (instrumentType === INSTRUMENT_TYPE_MAP.diary) {
    schedule.recurring = {
      interval: 1,
      interval_type: 'days',
      recurrences: 2,
      scheme: 'n-days',
      visible_days: 1,
    }
    schedule.deploy = {
      first_login: {
        interval: 1,
        interval_type: 'days',
      },
      time_of_day: '09:00',
    }
  }
  if (INSTRUMENT_TYPES_WITH_SCHEDULE_PLATFORM.includes(instrumentType)) {
    schedule.platform_type = SCHEDULE_PLATFORM_TYPE.all
  }
  return schedule
}

/**
 * Deep-clone a schedule entry for safe editing without mutating the list.
 */
export const cloneSchedule = schedule => {
  try {
    return JSON.parse(JSON.stringify(schedule))
  } catch (e) {
    return { ...schedule }
  }
}
